import React, { useEffect, useState } from 'react';
import { THIRD_PARTY_CONFIG } from "../../../../app_constant";
import { GET_PRODUCT_PRICES } from "../../../api/access_code";
import { GET_COUNTRY_CODE } from "../../../api/firebase_user";
import { getCurrency } from "../../../constant/currencyUtils";
import { PricingGrid } from "../../choosePlans/components";

const Pricing = ({ theme }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPrices();
  }, []);

  const getPrices = async () => {
    try {
      const countryCode = await GET_COUNTRY_CODE();
      const currencyData = getCurrency(countryCode);
      const pricesData = await GET_PRODUCT_PRICES(
        THIRD_PARTY_CONFIG?.STRIPE_PRODUCT_ID,
        currencyData
      );
      if (pricesData?.status) {
        const processedProducts = processProducts(pricesData.data);
        setProducts(processedProducts);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const processProducts = (rawProducts) => {
    const periods = ['Monthly', 'Quarterly', 'Semi-Annual', 'Annual'];
    const processedProducts = [];

    periods.forEach(period => {
      const subscriptionProduct = findProductByPeriod(rawProducts, period, 'recurring');
      const oneTimeProduct = findProductByPeriod(rawProducts, period, 'one_time');
      
      if (subscriptionProduct || oneTimeProduct) {
        processedProducts.push({
          period,
          subscription: subscriptionProduct,
          oneTime: oneTimeProduct,
          isPopular: period === 'Quarterly',
          isBestValue: period === 'Annual'
        });
      }
    });

    return processedProducts;
  };

  const findProductByPeriod = (products, period, type) => {
    return products.find(product => {
      if (product.type !== type || !product.active) return false;
      
      if (type === 'recurring') {
        switch (period) {
          case 'Monthly':
            return product.interval === 'month' && product.interval_count === 1;
          case 'Quarterly':
            return product.interval === 'month' && product.interval_count === 3;
          case 'Semi-Annual':
            return product.interval === 'month' && product.interval_count === 6;
          case 'Annual':
            return product.interval === 'year' && product.interval_count === 1;
          default:
            return false;
        }
      } else {
        switch (period) {
          case 'Monthly':
            return product.description === '1';
          case 'Quarterly':
            return product.description === '3';
          case 'Semi-Annual':
            return product.description === '6';
          case 'Annual':
            return product.description === '12';
          default:
            return false;
        }
      }
    });
  };

  // Dummy checkout handler since this is just for information
  const handleCheckout = () => {
    // This is just for information display, no actual checkout
    console.log('Checkout functionality not available on pricing page');
  };

  const styles = {
    container: {
      marginTop: '15px',
      scrollMarginTop: '80px', // Add scroll margin to account for fixed header
      paddingTop: '20px', // Add some top padding for better spacing
      textAlign: 'center',
      position: 'relative'
    },
    content: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    header: {
      textAlign: 'center',
      marginBottom: '3rem'
    },
    title: {
      fontSize: 'clamp(1.75rem, 5vw, 2.5rem)',
      fontWeight: '800',
      color: theme?.text?.primary,
      marginBottom: '0.75rem'
    },
    subtitle: {
      fontSize: 'clamp(0.95rem, 2.5vw, 1.125rem)',
      color: theme?.text?.secondary,
      maxWidth: '800px',
      margin: '0 auto',
      marginBottom: '1.5rem',
      lineHeight: '1.6',
      padding: '0 0.5rem'
    },
    pricingSection: {
      background: `linear-gradient(135deg, ${theme?.white}, ${theme?.gray[500]})`,
      padding: '6rem 2rem',
    },
    containerStyle: {
      maxWidth: '1200px',
      margin: '0 auto',
    },
    sectionHeader: {
      textAlign: 'center',
      marginBottom: '2.5rem'
    },
    sectionTitle: {
      fontSize: '2.5rem',
      fontWeight: '700',
      color: `${theme?.black}`,
      marginBottom: '0.75rem'
    },
    sectionSubtitle: {
      fontSize: '1.1rem',
      color: `${theme?.black}`,
      maxWidth: '600px',
      margin: '0 auto'
    }
  };

  if (loading) {
    return (
      <section id="pricing" style={styles.pricingSection}>
        <div style={styles.containerStyle}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Loading Pricing...</h2>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="pricing" style={styles.container}>
      <div style={styles.content}>
        <div style={styles.header}>
          <h2 style={styles.title}>Flexible Plans for Every Learner</h2>
          <p style={styles.subtitle}>Choose between one-time access or subscription savings. All plans include full premium features.</p>
        </div>

        <PricingGrid 
          products={products}
          promoDiscount={0}
          onCheckout={handleCheckout}
          theme={theme}
          isInfoOnly={true}
        />
      </div>
    </section>
  );
};

export default Pricing;